export const detectContentType = (content) => {
  if (!content) return "text";

  // Check for code patterns
  const codePatterns = [
    /^```(\w+)/m, // Code blocks
    /function\s+\w+\s*\(/m, // JavaScript functions
    /def\s+\w+\s*\(/m, // Python functions
    /class\s+\w+/m, // Class definitions
    /<html|<div|<script/i, // HTML
    /\.(js|py|html|css|json)$/i, // File extensions
  ];

  for (const pattern of codePatterns) {
    if (pattern.test(content)) {
      return "code";
    }
  }

  // Check for markdown patterns
  const markdownPatterns = [
    /^#{1,6}\s/m, // Headers
    /\*\*.*\*\*/m, // Bold
    /\[.*\]\(.*\)/m, // Links
    /^[-*+]\s/m, // Lists
  ];

  for (const pattern of markdownPatterns) {
    if (pattern.test(content)) {
      return "markdown";
    }
  }

  return "text";
};

export const detectLanguage = (content) => {
  const patterns = {
    javascript: [
      /function\s+\w+\s*\(/,
      /const\s+\w+\s*=/,
      /=>\s*{/,
      /\.map\(|\.filter\(|\.reduce\(/,
    ],
    python: [
      /def\s+\w+\s*\(/,
      /import\s+\w+/,
      /from\s+\w+\s+import/,
      /if\s+__name__\s*==\s*["']__main__["']/,
    ],
    html: [/<html|<head|<body|<div|<span/i, /<\/\w+>/, /<!DOCTYPE/i],
    css: [/\{[^}]*\}/, /\.[a-zA-Z-]+\s*\{/, /#[a-zA-Z-]+\s*\{/],
    json: [/^\s*{/, /"\w+"\s*:/, /^\s*\[.*\]\s*$/],
  };

  for (const [language, languagePatterns] of Object.entries(patterns)) {
    if (languagePatterns.some((pattern) => pattern.test(content))) {
      return language;
    }
  }

  return "text";
};

export const formatContentForCanvas = (content, type) => {
  if (type === "code") {
    // Remove code block markers if present
    return content.replace(/^```[\w]*\n?/, "").replace(/\n?```$/, "");
  }

  return content;
};

export const createCanvasMessage = (content, type, language, title) => {
  return {
    id: Date.now().toString(),
    type: "canvas",
    content: `Canvas created: ${title}`,
    canvasData: {
      content,
      type,
      language,
      title,
      createdAt: new Date().toISOString(),
    },
    timestamp: Date.now(),
    role: "assistant",
  };
};

// Utility functions for canvas operations

/**
 * Convert BlockNote blocks to markdown format
 * @param {Array} blocks - Array of BlockNote blocks
 * @returns {string} - Markdown formatted string
 */
export function convertBlocksToMarkdown(blocks) {
  if (!blocks || !Array.isArray(blocks)) {
    return "";
  }

  const lines = [];

  blocks.forEach((block, blockIndex) => {
    const markdown = convertBlockToMarkdown(block);
    if (markdown) {
      lines.push(markdown);

      // Add extra line break after certain block types for better formatting
      if (shouldAddExtraLineBreak(block, blocks[blockIndex + 1])) {
        lines.push("");
      }
    }
  });

  return lines.join("\n").trim();
}

/**
 * Convert a single BlockNote block to markdown
 * @param {Object} block - BlockNote block object
 * @returns {string} - Markdown string for the block
 */
function convertBlockToMarkdown(block) {
  if (!block || !block.type) {
    return "";
  }

  switch (block.type) {
    case "heading":
      return convertHeadingToMarkdown(block);
    case "paragraph":
      return convertParagraphToMarkdown(block);
    case "bulletListItem":
      return convertBulletListToMarkdown(block);
    case "numberedListItem":
      return convertNumberedListToMarkdown(block);
    case "checkListItem":
      return convertCheckListToMarkdown(block);
    case "codeBlock":
      return convertCodeBlockToMarkdown(block);
    case "quote":
      return convertQuoteToMarkdown(block);
    case "table":
      return convertTableToMarkdown(block);
    case "image":
      return convertImageToMarkdown(block);
    default:
      // For unknown block types, try to extract text content
      return convertGenericBlockToMarkdown(block);
  }
}

/**
 * Convert heading block to markdown
 */
function convertHeadingToMarkdown(block) {
  const level = block.props?.level || 1;
  const content = convertInlineContentToMarkdown(block.content || []);
  const hashes = "#".repeat(Math.min(level, 6));
  return `${hashes} ${content}`;
}

/**
 * Convert paragraph block to markdown
 */
function convertParagraphToMarkdown(block) {
  const content = convertInlineContentToMarkdown(block.content || []);
  return content || "";
}

/**
 * Convert bullet list item to markdown
 */
function convertBulletListToMarkdown(block) {
  const content = convertInlineContentToMarkdown(block.content || []);
  return `- ${content}`;
}

/**
 * Convert numbered list item to markdown
 */
function convertNumberedListToMarkdown(block) {
  const content = convertInlineContentToMarkdown(block.content || []);
  // Note: We use "1." for all items as markdown auto-numbers
  return `1. ${content}`;
}

/**
 * Convert check list item to markdown
 */
function convertCheckListToMarkdown(block) {
  const content = convertInlineContentToMarkdown(block.content || []);
  const checked = block.props?.checked || false;
  const checkbox = checked ? "[x]" : "[ ]";
  return `- ${checkbox} ${content}`;
}

/**
 * Convert code block to markdown
 */
function convertCodeBlockToMarkdown(block) {
  const language = block.props?.language || "";
  const content = extractTextFromContent(block.content || []);
  return `\`\`\`${language}\n${content}\n\`\`\``;
}

/**
 * Convert quote block to markdown
 */
function convertQuoteToMarkdown(block) {
  const content = convertInlineContentToMarkdown(block.content || []);
  return `> ${content}`;
}

/**
 * Convert table block to markdown
 */
function convertTableToMarkdown(block) {
  if (!block.content || !block.content.rows) {
    return "";
  }

  const rows = block.content.rows;
  if (rows.length === 0) {
    return "";
  }

  const lines = [];

  // Convert each row
  rows.forEach((row, rowIndex) => {
    const cells = row.cells || [];
    const cellTexts = cells.map((cell) =>
      convertInlineContentToMarkdown(cell || [])
    );
    lines.push(`| ${cellTexts.join(" | ")} |`);

    // Add separator row after header (first row)
    if (rowIndex === 0) {
      const separators = cells.map(() => "---");
      lines.push(`| ${separators.join(" | ")} |`);
    }
  });

  return lines.join("\n");
}

/**
 * Convert image block to markdown
 */
function convertImageToMarkdown(block) {
  const url = block.props?.url || "";
  const alt = block.props?.alt || "";
  const caption = block.props?.caption || "";

  if (!url) {
    return "";
  }

  let markdown = `![${alt}](${url})`;
  if (caption) {
    markdown += `\n*${caption}*`;
  }

  return markdown;
}

/**
 * Convert generic/unknown block to markdown by extracting text
 */
function convertGenericBlockToMarkdown(block) {
  const content = convertInlineContentToMarkdown(block.content || []);
  return content;
}

/**
 * Convert inline content array to markdown string
 * @param {Array} content - Array of inline content objects
 * @returns {string} - Markdown formatted string
 */
function convertInlineContentToMarkdown(content) {
  if (!Array.isArray(content)) {
    return "";
  }

  return content
    .map((item) => {
      if (item.type === "text") {
        return formatTextWithStyles(item.text || "", item.styles || {});
      } else if (item.type === "link") {
        const linkText = convertInlineContentToMarkdown(item.content || []);
        const href = item.href || "";
        return `[${linkText}](${href})`;
      } else if (item.type === "hardBreak") {
        return "\n";
      } else {
        // For other inline types, just extract text
        return item.text || "";
      }
    })
    .join("");
}

/**
 * Format text with markdown styling
 * @param {string} text - Plain text
 * @param {Object} styles - Style object with boolean flags
 * @returns {string} - Styled markdown text
 */
function formatTextWithStyles(text, styles) {
  if (!text) return "";

  let formatted = text;

  // Apply styles in order: code, bold, italic, underline, strikethrough
  if (styles.code) {
    formatted = `\`${formatted}\``;
  } else {
    // Only apply other styles if not code
    if (styles.bold) {
      formatted = `**${formatted}**`;
    }
    if (styles.italic) {
      formatted = `*${formatted}*`;
    }
    if (styles.underline) {
      formatted = `__${formatted}__`;
    }
    if (styles.strike || styles.strikethrough) {
      formatted = `~~${formatted}~~`;
    }
  }

  return formatted;
}

/**
 * Extract plain text from content array
 * @param {Array} content - Content array
 * @returns {string} - Plain text
 */
function extractTextFromContent(content) {
  if (!Array.isArray(content)) {
    return "";
  }

  return content
    .map((item) => {
      if (item.type === "text") {
        return item.text || "";
      } else if (item.type === "hardBreak") {
        return "\n";
      }
      return "";
    })
    .join("");
}

/**
 * Determine if an extra line break should be added after a block
 * @param {Object} currentBlock - Current block
 * @param {Object} nextBlock - Next block (can be undefined)
 * @returns {boolean} - Whether to add extra line break
 */
function shouldAddExtraLineBreak(currentBlock, nextBlock) {
  if (!nextBlock) {
    return false; // No extra line break at the end
  }

  const currentType = currentBlock.type;
  const nextType = nextBlock.type;

  // Add extra line break after headings
  if (currentType === "heading") {
    return true;
  }

  // Add extra line break after code blocks
  if (currentType === "codeBlock") {
    return true;
  }

  // Add extra line break after quotes
  if (currentType === "quote") {
    return true;
  }

  // Add extra line break after tables
  if (currentType === "table") {
    return true;
  }

  // Add extra line break between different list types
  if (
    isListType(currentType) &&
    isListType(nextType) &&
    currentType !== nextType
  ) {
    return true;
  }

  // Add extra line break when transitioning from list to non-list
  if (isListType(currentType) && !isListType(nextType)) {
    return true;
  }

  return false;
}

/**
 * Check if block type is a list type
 * @param {string} blockType - Block type
 * @returns {boolean} - Whether it's a list type
 */
function isListType(blockType) {
  return ["bulletListItem", "numberedListItem", "checkListItem"].includes(
    blockType
  );
}

/**
 * Parse content to blocks (existing functionality)
 * @param {string} content - Content string (JSON or plain text)
 * @param {string} type - Content type
 * @returns {Array} - Array of blocks
 */
export function parseContentToBlocks(content, _type = "text") {
  if (!content) return [];

  // If content is already an array, return it
  if (Array.isArray(content)) {
    return content;
  }

  // If content is a string, try to parse it as JSON
  if (typeof content === "string") {
    try {
      const parsed = JSON.parse(content);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      // Not JSON, treat as plain text
      return [
        {
          id: `block_${Date.now()}`,
          type: "paragraph",
          props: {
            textColor: "default",
            backgroundColor: "default",
            textAlignment: "left",
          },
          content: [
            {
              type: "text",
              text: content,
              styles: {
                textColor: "default",
                backgroundColor: "default",
              },
            },
          ],
          children: [],
        },
      ];
    }
  }

  return [];
}

/**
 * Get file extension based on canvas type
 * @param {string} type - Canvas type
 * @returns {string} - File extension
 */
export function getFileExtension(type) {
  switch (type) {
    case "code":
      return "js"; // Default to JavaScript, could be enhanced to detect language
    case "markdown":
      return "md";
    case "html":
      return "html";
    case "spreadsheet":
      return "csv";
    case "text":
    default:
      return "md"; // Default to markdown for text content
  }
}

/**
 * Get MIME type based on canvas type
 * @param {string} type - Canvas type
 * @returns {string} - MIME type
 */
export function getMimeType(type) {
  switch (type) {
    case "code":
      return "text/javascript";
    case "markdown":
      return "text/markdown";
    case "html":
      return "text/html";
    case "spreadsheet":
      return "text/csv";
    case "text":
    default:
      return "text/plain";
  }
}

/**
 * Convert CSV content to markdown table format
 * @param {string} csvContent - CSV content string
 * @returns {string} - Markdown table formatted string
 */
export function convertCsvToMarkdownTable(csvContent) {
  if (!csvContent || typeof csvContent !== "string") {
    return "";
  }

  try {
    // Split CSV into lines and parse each line
    const lines = csvContent.trim().split("\n");
    if (lines.length === 0) {
      return "";
    }

    // Parse CSV lines (simple parsing - handles basic CSV)
    const parseCSVLine = (line) => {
      const result = [];
      let current = "";
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];

        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            // Escaped quote
            current += '"';
            i++; // Skip next quote
          } else {
            // Toggle quote state
            inQuotes = !inQuotes;
          }
        } else if (char === "," && !inQuotes) {
          // End of field
          result.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }

      // Add last field
      result.push(current.trim());
      return result;
    };

    // Parse all lines
    const rows = lines.map(parseCSVLine);

    // Filter out empty rows
    const nonEmptyRows = rows.filter((row) =>
      row.some((cell) => cell && cell.trim().length > 0)
    );

    if (nonEmptyRows.length === 0) {
      return "";
    }

    // Build markdown table
    const markdownLines = [];

    // Add each row as a table row
    nonEmptyRows.forEach((row, index) => {
      // Escape pipe characters in cell content
      const escapedRow = row.map((cell) => (cell || "").replace(/\|/g, "\\|"));

      markdownLines.push(`| ${escapedRow.join(" | ")} |`);

      // Add separator row after header (first row)
      if (index === 0) {
        const separators = escapedRow.map(() => "---");
        markdownLines.push(`| ${separators.join(" | ")} |`);
      }
    });

    return markdownLines.join("\n");
  } catch (error) {
    console.error("Error converting CSV to markdown table:", error);
    return csvContent; // Return original content if conversion fails
  }
}
